import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { upsellAutoModal, type UpsellSeenKind } from 'fym-shared/src/music24/watermarkUpsell';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { useMarkUpsellSeen } from '../useMarkUpsellSeen';
import { useUpsellFacts } from '../useUpsellFacts';
import { hadAutoModalThisSession, markAutoModalThisSession } from './autoModalSession';
import { isAnyDialogOpen, useAutoModalBlocked } from './useAutoModalBlocked';

/**
 * The wait before an automatic modal opens: the page settles first (its own dialogs, the welcome
 * redirect), so the modal never opens under or over something else.
 */
export const AUTO_MODAL_DELAY_MS = 1500;

/**
 * The first-file reveal opens only on Home (QA 2026-10-28, imp-6): on the release page or in the
 * Studio the artist is busy with the first files, and a modal there cut into the first win. The
 * banner, the viewer note and the choice of a download keep the offer on those pages.
 */
export function autoModalFitsPage(kind: UpsellSeenKind, pathname: string): boolean {
  return kind !== 'firstFile' || pathname.split('/').includes(SMLRouteNames.home);
}

/** The input that shows the user works on the page: a tap, a click, a key or a scroll. */
const INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel'] as const;

/**
 * True from the first tap, key or scroll on the current page (QA 2026-10-28, mobr2-2 and
 * impr2-1): a modal that opened then took the next tap ("Pick a look" did nothing). An automatic
 * modal opens only on a page that the user did not touch yet; else it waits for the next page.
 */
function useInteractedOnPage(pathname: string): { readonly current: boolean } {
  const interacted = useRef(false);
  useEffect(() => {
    interacted.current = false;
    const mark = () => {
      interacted.current = true;
    };
    INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, mark, { capture: true, passive: true }));
    return () => INTERACTION_EVENTS.forEach((name) => window.removeEventListener(name, mark, { capture: true }));
  }, [pathname]);
  return interacted;
}

export interface UpsellAutoModalState {
  /** The automatic modal that is open, or null. */
  open: UpsellSeenKind | null;
  close: () => void;
}

/**
 * The one host rule of the automatic upsell modals: the first-file reveal (design a1) and the
 * takeover of the last trial day and of the first Free visit (b3). `upsellAutoModal` (fym-shared)
 * says which one may open for these facts: only an owner, never the demo artist, never the native
 * web view, each one time for each user (the server flags). This hook adds the page rules: at most
 * one in a session, never over another dialog, never in the tour or on the welcome page, never on a
 * page that the user tapped, typed or scrolled already, and the first-file reveal only on Home
 * (`autoModalFitsPage`). When one
 * opens, `onboarding.markUpsellSeen` writes its flag.
 */
export function useUpsellAutoModal(): UpsellAutoModalState {
  const { facts, isReady } = useUpsellFacts();
  const isBlocked = useAutoModalBlocked();
  const markSeen = useMarkUpsellSeen();
  const { pathname } = useLocation();
  const interacted = useInteractedOnPage(pathname);
  const [open, setOpen] = useState<UpsellSeenKind | null>(null);
  const factKind = isReady ? upsellAutoModal(facts) : null;
  const kind = factKind !== null && autoModalFitsPage(factKind, pathname) ? factKind : null;

  useEffect(() => {
    if (open !== null || kind === null || isBlocked || hadAutoModalThisSession()) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      // Read the page again at the moment of opening: a dialog may be open now, or the user may
      // work on the page already. A modal never opens under a finger (QA mobr2-2).
      if (hadAutoModalThisSession() || isAnyDialogOpen() || interacted.current) {
        return;
      }
      markAutoModalThisSession();
      setOpen(kind);
      markSeen(kind);
    }, AUTO_MODAL_DELAY_MS);
    return () => window.clearTimeout(timer);
    // `pathname`: a modal that could not open tries again on the next page.
  }, [open, kind, isBlocked, markSeen, pathname, interacted]);

  const close = useCallback(() => setOpen(null), []);
  return { open, close };
}
